// Services
import { sendAmplitudeEvent } from './amplitudeService';
import { getRememberedBetterAuthSessionUser } from '../auth/authService';

// Utils
import { getTimestamp, TIMES } from '../../common/utils/timeUtil';
import { NewRelicPageActions } from './newRelicConstants';
import { sendNewRelicPageAction } from './newRelicUtils';

// Constants
import { EVENT_TYPE_NAMES } from '../../common/analytics/amplitudeEventTypesUtil';

/**
 * Reports a forced logout to Amplitude and New Relic when a returning user loads the app
 * unauthenticated but a remembered Better Auth session user is still cached, i.e. their session
 * lapsed.
 *
 * Genuine expiry is server-side indistinguishable from a brand-new visitor (the cookie and Redis
 * record are evicted together), but the cache persists client-side (no TTL). The cache is cleared on
 * explicit logout, so a deliberate sign-out does not reach here. Amplitude's persistent device
 * identity then lets us measure whether the user keeps using Milanote afterwards or bounces, and the
 * New Relic page action gives the same signal in our operational dashboards.
 *
 * The cache also carries the server-authoritative session expiry, so we can report how many days
 * after expiry the user returned (daysSinceExpiry).
 *
 * The caller clears the invalid session immediately after (clearInvalidBetterAuthSession), so a lapse
 * is counted once rather than on every reload; a successful re-authentication re-writes the cache,
 * re-arming detection for any future lapse.
 */
export const trackForcedLogoutIfLapsed = (): void => {
    const rememberedUser = getRememberedBetterAuthSessionUser();
    if (!rememberedUser) return;

    const { sessionExpiresAt } = rememberedUser;

    const eventProperties: Record<string, number> =
        typeof sessionExpiresAt === 'number'
            ? { daysSinceExpiry: Math.floor((getTimestamp() - sessionExpiresAt) / TIMES.DAY) }
            : {};

    sendAmplitudeEvent({
        eventType: EVENT_TYPE_NAMES.FORCED_LOGOUT,
        eventProperties,
    });
    sendNewRelicPageAction(NewRelicPageActions.FORCED_LOGOUT, eventProperties);
};
